import { z } from 'zod';
/**
 * Shared primitive builders and cross-cutting enums (E5/E6).
 *
 * Convention (matches the reference `documentSchema`, CODE_GUIDELINES §2):
 * - Entity/wire fields are **camelCase** (app-facing shape). The Drizzle layer
 *   (@xdocs/db, F0d) maps snake_case DB columns ↔ these camelCase fields.
 * - Timestamps parse via `z.coerce.date()` (accepts ISO strings off the wire and
 *   `Date` off the driver).
 * - Ids are RFC-4122 UUIDs.
 * - Money is ALWAYS integer minor units + an ISO-4217 currency code — never a
 *   pre-formatted string (platform.md / §20 i18n; formatting happens at the edge
 *   via @xdocs/i18n `formatCurrency`).
 *
 * Entity schemas (DB rows read back) carry NO validation `message` — a failure
 * there is an internal bug, not a user field error. Only INPUT DTOs (user-facing
 * forms / WS request payloads) carry catalog-key messages (see ./errors.ts and
 * the `*.dto` schemas). This mirrors the reference exactly.
 */
/** RFC-4122 UUID string. */
export const uuid = z.string().uuid();
/** Nullable UUID (e.g. a FK that may be unset). */
export const nullableUuid = uuid.nullable();
/** Timestamp field: ISO string on the wire, `Date` from the pg driver. */
export const timestamp = z.coerce.date();
/**
 * Launch locale set. THE validation authority for `users.locale` and every
 * locale-typed field/DTO.
 *
 * NOTE (contract coupling): `@xdocs/i18n` (F0j) owns the runtime `LOCALES`
 * const + display catalogs. Both are Phase-0 leaves and neither may import the
 * other (dependency graph, §3), so this enum is duplicated by construction. It
 * MUST stay in lockstep with `@xdocs/i18n` `LOCALES` — CI keyset-diff (F0j)
 * guards catalog drift; a launch-locale change edits BOTH.
 */
export const localeSchema = z.enum(['en', 'ro']);
/** ISO-4217 alphabetic currency code (e.g. 'USD', 'EUR', 'RON'). */
export const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);
/** Integer amount in minor units (cents/bani). Never a float, never formatted. */
export const minorUnits = z.number().int();
/** Non-empty jsonb object bag used for structured metadata columns. */
export const jsonObject = z.record(z.string(), z.unknown());
/** BCP-47 language tag as used by `documents.language` / translation targets. */
export const bcp47 = z.string().min(2).max(35);
